import { FC } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, ButtonBase, CircularProgress } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import {
  dailyNavStatusNeedsAction,
  getDailyNavReviewState,
} from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { ReviewPriceCellProps } from './ReviewPriceCell.props';

// One pulse, not a loop: it confirms the save landed and then gets out of the
// way. Scale stays small because the button is 18px tall inside a grid row.
const savedPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.25); }
  40% { transform: scale(1.12); box-shadow: 0 0 0 4px rgba(0, 0, 0, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
`;

const ReviewPriceCell: FC<ReviewPriceCellProps> = ({ row, onReviewClick, canReview, saveFeedback }) => {
  // One resolver for label and colour, so they cannot disagree.
  const reviewState = getDailyNavReviewState(row.record);
  const needsReview = dailyNavStatusNeedsAction(reviewState.status);

  const baseColor = reviewState.color;
  // Read-only buttons stay flat on hover; interactive ones darken.
  const hoverColor = canReview ? reviewState.hoverColor : baseColor;

  return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
      <ButtonBase
        id={`review-price-button-${row.companyId}`}
        // Review Price is for firm editors and staff; others get an inert, read-only button.
        disabled={!canReview}
        onClick={canReview ? () => onReviewClick(row) : undefined}
        data-save-feedback={saveFeedback}
        sx={{
          alignItems: 'center',
          animation: saveFeedback === 'saved' ? `${savedPulse} 1.5s ease-out 1` : 'none',
          backgroundColor: baseColor,
          borderRadius: '4px',
          color: reviewState.textColor,
          cursor: canReview ? 'pointer' : 'default',
          display: 'inline-flex',
          fontSize: 10,
          fontWeight: 600,
          gap: '4px',
          height: 18,
          lineHeight: 1,
          padding: '2px 8px',
          whiteSpace: 'nowrap',
          '&:hover': {
            backgroundColor: hoverColor,
          },
        }}>
        {/* Only the state asking for action gets the glyph. */}
        {needsReview && <WarningAmberIcon sx={{ fontSize: 11 }} />}
        {reviewState.label}
        {saveFeedback === 'saving' && (
          <CircularProgress
            size={9}
            thickness={6}
            data-testid={`review-price-saving-${row.companyId}`}
            aria-label="Refreshing this row"
            // Inherits the button's own text colour, which changes with status.
            sx={{ color: 'inherit' }}
          />
        )}
      </ButtonBase>
    </Box>
  );
};

export default ReviewPriceCell;
